import { z } from 'zod';
import { severitySchema } from './shared/severity';
/*
  Agent session timeline (KIR-4445).

  Two sides:
  - Ingest: the endpoint agent posts one session event per hook event to
    POST /api/v2/agent/session-events. Identity (organization, user, machine)
    comes from the authenticated request, never from this body.
  - Read: an alert occurrence opens the ordered timeline of the session it came
    from. Every read is scoped through the occurrence the caller may see; there
    is no endpoint that takes a client-supplied session id.
*/
/** Longest `target` the ingest endpoint stores. */
export const SESSION_EVENT_TARGET_MAX_LENGTH = 1000;
/** Longest `target` a timeline row carries (display only). */
export const SESSION_EVENT_TARGET_DISPLAY_LENGTH = 300;
/** Server clamp for a timeline page. */
export const SESSION_TIMELINE_MAX_LIMIT = 100;
/** Rows on each side of the highlighted event in the opening window. */
export const SESSION_TIMELINE_ANCHOR_SPAN = 25;
/** Default page size when scrolling in either direction. */
export const SESSION_TIMELINE_PAGE_SIZE = 50;
/** Largest ingest body, matching the agent's per-event cap. */
export const SESSION_EVENT_MAX_BODY_BYTES = 2 * 1024 * 1024;
const clip = (max) => z.string().transform(s => s.slice(0, max));
// Descriptive fields are clipped, not rejected: the agent counts runes, not
// UTF-16 units, and a rejected event is lost from the timeline for good.
// Identity fields stay strict.
export const sessionEventIngestRequestSchema = z.object({
    eventId: z.string().min(1).max(128),
    sessionId: z.string().min(1).max(256),
    platform: z.string().min(1).max(64),
    eventType: z.string().min(1).max(64),
    platformEventName: clip(128).optional(),
    occurredAt: z.string().datetime({ offset: true }),
    target: clip(SESSION_EVENT_TARGET_MAX_LENGTH).optional(),
    model: clip(256).optional(),
    permissionMode: clip(64).optional(),
    cwd: clip(4096).optional(),
    workspaceRoots: z
        .array(clip(4096))
        .transform(roots => roots.slice(0, 50))
        .optional(),
    repoUrl: clip(2048).optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
});
/**
 * POST /api/hooks/session-events: the Claude Desktop (CoWork) hook plugin
 * posts with an organization key, so it names its user the way its scans do.
 * The server sets `platform`; the body's value is ignored.
 */
export const hookSessionEventIngestRequestSchema = sessionEventIngestRequestSchema.extend({
    extraData: z
        .object({ userEmail: z.string().max(1024).optional() })
        .optional(),
});
export const sessionEventIngestResponseSchema = z.object({
    /** False when the event id was already stored (a retry). Still success. */
    stored: z.boolean(),
});
/**
 * What happened to the alert's own event.
 * - `found`: on the timeline.
 * - `pending`: not stored yet; events are sent after the synchronous scan, so a
 *   short delay is normal. The client refetches a bounded number of times.
 * - `not_recorded`: never arrived (the client decides this once the refetch
 *   budget is spent; the server never returns it).
 * - `expired`: retention deleted the part of the session it belonged to.
 */
export const sessionHighlightStateSchema = z.enum([
    'found',
    'pending',
    'not_recorded',
    'expired',
]);
export const sessionTimelineBlockSchema = z.object({
    findingId: z.string(),
    type: z.string(),
    subPolicyKey: z.string().nullable(),
    description: z.string(),
    outcome: z.string(),
});
/** One row of the timeline. Payloads are never on a page; see the event endpoint. */
export const sessionTimelineEventSchema = z.object({
    eventId: z.string(),
    occurredAt: z.string().datetime(),
    eventType: z.string(),
    platformEventName: z.string().nullable(),
    /** Set on ToolObserved rows only, from the payload's toolName. */
    toolName: z.string().nullable(),
    target: z.string().nullable(),
    /** Findings raised on this event; the first blocking one is the rationale. */
    findings: z.array(sessionTimelineBlockSchema),
});
export const sessionTimelineHeaderSchema = z.object({
    sessionId: z.string(),
    platform: z.string(),
    /** Null once the session's user was deleted. */
    user: z
        .object({
        id: z.string(),
        email: z.string().nullable(),
        name: z.string().nullable(),
    })
        .nullable(),
    endpoint: z.object({
        machineId: z.string().nullable(),
        hostname: z.string().nullable(),
        os: z.string().nullable(),
    }),
    repository: z.string().nullable(),
    project: z
        .object({ id: z.string(), name: z.string(), slug: z.string().nullable() })
        .nullable(),
    /** The working directory, or the first workspace folder when the agent
     * reports no working directory; the same rule as a secret alert's
     * Workspace. */
    workspace: z.string().nullable(),
    model: z.string().nullable(),
    startedAt: z.string().datetime(),
    lastEventAt: z.string().datetime().nullable(),
    eventCount: z.number().int().min(0),
    blockedCount: z.number().int().min(0),
    retained: z.boolean(),
    /** When retention deletes the session; null while it is kept. */
    expiresAt: z.string().datetime().nullable(),
});
export const sessionTimelineDirectionSchema = z.enum(['before', 'after']);
export const sessionTimelineQuerySchema = z.object({
    /** Absent: the opening window anchored on the highlighted event. */
    cursor: z.string().min(1).max(512).optional(),
    direction: sessionTimelineDirectionSchema.optional(),
    limit: z.coerce.number().int().min(1).max(1000).optional(),
    /**
     * One id per opening of the timeline. The opening read is audited once per
     * id: a refetch that sends the same id does not add another view row.
     */
    viewId: z.uuid().optional(),
});
export const sessionTimelineResponseSchema = z.object({
    events: z.array(sessionTimelineEventSchema),
    /**
     * Null at the start of the session. Scroll pages always carry the cursor
     * back toward where they were fetched from, so a client that dropped pages
     * can scroll back.
     */
    prevCursor: z.string().nullable(),
    /** Null at the newest event so far; a live session can still grow. */
    nextCursor: z.string().nullable(),
    highlight: z.object({
        eventId: z.string(),
        state: sessionHighlightStateSchema,
    }),
    /**
     * On the opening request (no cursor) whenever the session row exists; drives
     * the Keep button. Always null on scroll pages.
     */
    header: sessionTimelineHeaderSchema.nullable(),
});
export const sessionTimelineEventDetailSchema = z.object({
    eventId: z.string(),
    occurredAt: z.string().datetime(),
    eventType: z.string(),
    platformEventName: z.string().nullable(),
    target: z.string().nullable(),
    model: z.string().nullable(),
    permissionMode: z.string().nullable(),
    cwd: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()).nullable(),
});
export const sessionSummarySchema = z.object({
    sessionId: z.string(),
    /** False when the session row does not exist (yet, or anymore). */
    recorded: z.boolean(),
    eventCount: z.number().int().min(0),
    blockedCount: z.number().int().min(0),
    retained: z.boolean(),
    /** When retention deletes the session; null while kept or not recorded. */
    expiresAt: z.string().datetime().nullable(),
});
export const keepSessionResponseSchema = z.object({
    retained: z.literal(true),
    retainedAt: z.string().datetime(),
});
export const releaseSessionResponseSchema = z.object({
    retained: z.literal(false),
    /** When retention deletes the session now that the hold is lifted. */
    expiresAt: z.string().datetime(),
});
/*
  Session view (KIR-4785): every alert from the session an occurrence came
  from, across policies and alert groups. Addressed by the occurrence, like the
  timeline; excluded alerts are left out and muted ones are listed with `muted`.
*/
/** Server clamp for a Session view page. */
export const SESSION_ALERTS_MAX_LIMIT = 100;
/** Default Session view page size. */
export const SESSION_ALERTS_PAGE_SIZE = 50;
export const sessionAlertsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(SESSION_ALERTS_MAX_LIMIT)
        .optional(),
});
export const sessionAlertRowSchema = z.object({
    /** `findings.id`: opens this occurrence's Alert Variations. */
    occurrenceId: z.string(),
    /** The alert group's `finding_key`. */
    alertKey: z.string(),
    /** The session event's time, or the finding's `createdAt` without one. */
    occurredAt: z.string().datetime(),
    type: z.string(),
    /** Null for a legacy type no policy maps to. */
    policyKey: z.string().nullable(),
    subPolicyKey: z.string().nullable(),
    title: z.string(),
    /** Effective severity, with the group's override applied. */
    severity: severitySchema,
    outcome: z.string(),
    filePath: z.string().nullable(),
    location: z.string().nullable(),
    eventKind: z.string().nullable(),
    endpointName: z.string().nullable(),
    muted: z.boolean(),
});
/**
 * Computed over every listed alert, not the current page. On an empty list the
 * times and endpoint are null and `alertCount` is 0.
 */
export const sessionAlertsSummarySchema = z.object({
    /** Null once the session's user was deleted. */
    user: z
        .object({
        id: z.string(),
        email: z.string().nullable(),
        name: z.string().nullable(),
    })
        .nullable(),
    alertCount: z.number().int().min(0),
    firstAlertAt: z.string().datetime().nullable(),
    lastAlertAt: z.string().datetime().nullable(),
    /** The latest alert's stored endpoint name. */
    endpointName: z.string().nullable(),
});
export const sessionAlertsResponseSchema = z.object({
    sessionId: z.string(),
    /** Null when the session's events are gone (or were never stored). */
    header: sessionTimelineHeaderSchema.nullable(),
    summary: sessionAlertsSummarySchema,
    alerts: z.array(sessionAlertRowSchema),
    /** Equal to `summary.alertCount`. */
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    /** The occurrence the view was opened from. */
    openedOccurrenceId: z.string(),
});
/** Present on an alert occurrence when it can open a session timeline. */
export const alertSessionLinkSchema = z.object({
    sessionId: z.string(),
    sessionEventId: z.string(),
});
/** A grouped alert's link: its latest occurrence, which the session endpoints key on. */
export const groupedAlertSessionLinkSchema = alertSessionLinkSchema.extend({
    occurrenceId: z.string(),
});
//# sourceMappingURL=agent-sessions.js.map